/**
 * Advertiser API client — wraps `/api/advertiser/*` + the
 * `/api/auth/register-advertiser` flow.
 *
 * Mirrors api/src/routes/advertiser.js + the advertiser branch of
 * api/src/routes/auth.js. Every call attaches the access token from
 * `useAuthStore` as a Bearer header. The shared `api` wrapper already
 * unwraps the REST-SPEC envelope's `data` field, so callers receive
 * the inner payload directly.
 *
 * Types intentionally keep `targeting` loose (Record<string, unknown>)
 * because the underlying schema is owned by the API and may grow new
 * facets in later phases.
 */

import { api } from './api';
import { useAuthStore } from '../stores/authStore';
import type { AuthBundle } from './authApi';

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Types ──────────────────────────────────────────────────────────────────

export type AdvertiserStatus = 'pending' | 'active' | 'suspended';
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'ended' | 'archived';

export interface AdvertiserAccount {
  id: string;
  userId: string;
  companyName: string;
  contactEmail: string;
  status: AdvertiserStatus;
  createdAt?: string;
  updatedAt?: string;
}

export interface CampaignTargeting {
  calibers?: string[];
  brands?: string[];
  regions?: string[];
  useCases?: string[];
  priceRange?: { minCents?: number; maxCents?: number };
}

export interface Campaign {
  id: string;
  advertiserId: string;
  name: string;
  status: CampaignStatus;
  dailyBudgetCents: number | null;
  totalBudgetCents: number | null;
  spentCents: number;
  startsAt: string | null;
  endsAt: string | null;
  targeting: CampaignTargeting | null;
  createdAt?: string;
  updatedAt?: string;
  placements?: SponsoredPlacement[];
}

export interface SponsoredPlacement {
  id: string;
  campaignId: string;
  productListingId: string;
  bidCents: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CampaignCreateBody {
  name: string;
  status?: CampaignStatus;
  dailyBudgetCents?: number | null;
  totalBudgetCents?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  targeting?: CampaignTargeting | null;
  placements?: PlacementAddBody[];
}

export type CampaignPatchBody = Partial<Omit<CampaignCreateBody, 'placements'>>;

export interface PlacementAddBody {
  productListingId: string;
  bidCents: number;
}

export interface DashboardSummary {
  campaigns: {
    total: number;
    active: number;
    paused: number;
    ended: number;
    draft: number;
  };
  placementCount: number;
  totalSpendCents: number;
  impressions: number;
  clicks: number;
}

export interface CampaignPerformance {
  campaignId: string;
  name: string;
  status: CampaignStatus;
  totalBudgetCents: number | null;
  dailyBudgetCents: number | null;
  spentCents: number;
  impressions: number;
  clicks: number;
  ctr: number;
  averagePosition: number | null;
  note?: string;
}

// ── Reports (spec §11.5 / §11.6) ───────────────────────────────────────────

/**
 * Optional filters for every report request. Calibers are encoded as
 * repeated query params (`?calibers=9mm%20Luger&calibers=.223...`).
 */
export interface ReportQuery {
  calibers?: string[];
  startDate?: string;
  endDate?: string;
}

export interface ReportOpts {
  minCohort: number;
}

export interface ReportEnvelope<RowT> {
  data: {
    rows: RowT[];
    suppressedCohortCount: number;
    opts: ReportOpts;
  };
  generatedAt: string;
  source: string;
}

export interface SearchDemandRow {
  caliber: string;
  region: string;
  alertCount: number;
  watchlistCount: number;
}

export interface CaliberDemandRow {
  caliber: string;
  region: string;
  currentCount: number;
  sevenDaysAgoCount: number | null;
  trendNote: string;
}

export interface InventoryGapRow {
  caliber: string;
  region: string;
  alertCount: number;
  inStockListingCount: number;
  gap: number;
}

export interface AudienceInsightRow {
  useCase: string;
  region: string;
  alertCount: number;
}

export type SearchDemandReport = ReportEnvelope<SearchDemandRow>;
export type CaliberDemandReport = ReportEnvelope<CaliberDemandRow>;
export type InventoryGapsReport = ReportEnvelope<InventoryGapRow>;
export type AudienceInsightsReport = ReportEnvelope<AudienceInsightRow>;

export interface RegisterAdvertiserInput {
  email: string;
  password: string;
  companyName: string;
  contactEmail: string;
}

export interface RegisterAdvertiserBundle extends AuthBundle {
  advertiser: AdvertiserAccount;
}

// ── Endpoints ──────────────────────────────────────────────────────────────

export const advertiserApi = {
  // Registration ----------------------------------------------------------
  register(input: RegisterAdvertiserInput): Promise<RegisterAdvertiserBundle> {
    return api.post<RegisterAdvertiserBundle>('/auth/register-advertiser', input);
  },

  // Account ---------------------------------------------------------------
  getAccount(): Promise<{ account: AdvertiserAccount }> {
    return api.get<{ account: AdvertiserAccount }>('/advertiser/account', {
      headers: authHeaders(),
    });
  },
  updateAccount(
    patch: Partial<Pick<AdvertiserAccount, 'companyName' | 'contactEmail'>>
  ): Promise<{ account: AdvertiserAccount }> {
    return api.patch<{ account: AdvertiserAccount }>('/advertiser/account', patch, {
      headers: authHeaders(),
    });
  },

  // Campaigns -------------------------------------------------------------
  listCampaigns(filters: { status?: CampaignStatus } = {}): Promise<{ campaigns: Campaign[] }> {
    const query = filters.status ? `?status=${encodeURIComponent(filters.status)}` : '';
    return api.get<{ campaigns: Campaign[] }>(`/advertiser/campaigns${query}`, {
      headers: authHeaders(),
    });
  },
  createCampaign(body: CampaignCreateBody): Promise<{ campaign: Campaign }> {
    return api.post<{ campaign: Campaign }>('/advertiser/campaigns', body, {
      headers: authHeaders(),
    });
  },
  getCampaign(id: string): Promise<{ campaign: Campaign }> {
    return api.get<{ campaign: Campaign }>(`/advertiser/campaigns/${id}`, {
      headers: authHeaders(),
    });
  },
  updateCampaign(id: string, patch: CampaignPatchBody): Promise<{ campaign: Campaign }> {
    return api.patch<{ campaign: Campaign }>(`/advertiser/campaigns/${id}`, patch, {
      headers: authHeaders(),
    });
  },
  pauseCampaign(id: string): Promise<{ campaign: Campaign }> {
    return api.post<{ campaign: Campaign }>(`/advertiser/campaigns/${id}/pause`, undefined, {
      headers: authHeaders(),
    });
  },
  resumeCampaign(id: string): Promise<{ campaign: Campaign }> {
    return api.post<{ campaign: Campaign }>(`/advertiser/campaigns/${id}/resume`, undefined, {
      headers: authHeaders(),
    });
  },
  getPerformance(id: string): Promise<{ performance: CampaignPerformance }> {
    return api.get<{ performance: CampaignPerformance }>(
      `/advertiser/campaigns/${id}/performance`,
      { headers: authHeaders() }
    );
  },

  // Placements ------------------------------------------------------------
  listPlacements(campaignId: string): Promise<{ placements: SponsoredPlacement[] }> {
    return api.get<{ placements: SponsoredPlacement[] }>(
      `/advertiser/campaigns/${campaignId}/placements`,
      { headers: authHeaders() }
    );
  },
  addPlacement(
    campaignId: string,
    body: PlacementAddBody
  ): Promise<{ placement: SponsoredPlacement }> {
    return api.post<{ placement: SponsoredPlacement }>(
      `/advertiser/campaigns/${campaignId}/placements`,
      body,
      { headers: authHeaders() }
    );
  },
  removePlacement(campaignId: string, placementId: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(
      `/advertiser/campaigns/${campaignId}/placements/${placementId}`,
      { headers: authHeaders() }
    );
  },

  // Dashboard -------------------------------------------------------------
  getDashboard(): Promise<{ account: AdvertiserAccount; summary: DashboardSummary }> {
    return api.get<{ account: AdvertiserAccount; summary: DashboardSummary }>(
      '/advertiser/dashboard',
      { headers: authHeaders() }
    );
  },

  // Reports ---------------------------------------------------------------
  getSearchDemandReport(query: ReportQuery = {}): Promise<{ report: SearchDemandReport }> {
    return api.get<{ report: SearchDemandReport }>(
      `/advertiser/reports/search-demand${buildReportQuery(query)}`,
      { headers: authHeaders() }
    );
  },
  getCaliberDemandReport(query: ReportQuery = {}): Promise<{ report: CaliberDemandReport }> {
    return api.get<{ report: CaliberDemandReport }>(
      `/advertiser/reports/caliber-demand${buildReportQuery(query)}`,
      { headers: authHeaders() }
    );
  },
  getInventoryGapsReport(query: ReportQuery = {}): Promise<{ report: InventoryGapsReport }> {
    return api.get<{ report: InventoryGapsReport }>(
      `/advertiser/reports/inventory-gaps${buildReportQuery(query)}`,
      { headers: authHeaders() }
    );
  },
  getAudienceInsightsReport(query: ReportQuery = {}): Promise<{ report: AudienceInsightsReport }> {
    return api.get<{ report: AudienceInsightsReport }>(
      `/advertiser/reports/audience-insights${buildReportQuery(query)}`,
      { headers: authHeaders() }
    );
  },
};

/**
 * Serialize a `ReportQuery` into a URL query string (with leading `?`),
 * or the empty string when no params are set. Calibers are emitted as
 * repeated params so the Express layer parses them as an array.
 */
function buildReportQuery(query: ReportQuery): string {
  const params = new URLSearchParams();
  if (query.calibers && query.calibers.length > 0) {
    for (const c of query.calibers) params.append('calibers', c);
  }
  if (query.startDate) params.set('startDate', query.startDate);
  if (query.endDate) params.set('endDate', query.endDate);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export default advertiserApi;
